import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  isDevMode,
  Output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditorComponent, EditorModule } from '@progress/kendo-angular-editor';

/**
 * DORMANT MOBILE ENHANCEMENT, NOT the Bug 30748 fix. 30748 (per-line/bullet
 * caps visible on desktop Chrome, the only supported platform) is closed by
 * the CSS ::first-letter rules on the consumer classes in OM's styles.scss;
 * these attributes are virtual-keyboard hints that do nothing on a hardware
 * keyboard. They're kept because they cost nothing and make a mobile keyboard
 * behave sensibly if the app is ever opened on one. Kendo v22 exposes no
 * `autocapitalize` passthrough, hence setting them on the contenteditable.
 *
 * Deliberately does NOT set `spellcheck`: on a ProseMirror contenteditable it
 * drives native spellcheck on every input, the typing-lag source flagged in
 * the PR 2584 review. The spec pins its absence.
 */
export function applyEditorAutocapitalizeAttrs(
  contentEl: HTMLElement,
  renderer: Renderer2
): void {
  renderer.setAttribute(contentEl, 'autocapitalize', 'sentences');
  renderer.setAttribute(contentEl, 'autocorrect', 'on');
}

/**
 * Shared Work Scope rich-text editor (30778), consolidating the three inline
 * kendo-editor copies (SRF project-details, SRF required-fields-top, Estimate
 * Builder job-info) into one component.
 *
 * FULL ENCAPSULATION IS LOAD-BEARING, NOT COSMETIC. kendo-editor finds its
 * toolbar through a ContentChild query and kendo-toolbar renders tools only
 * from its own content query, and neither query crosses a component view
 * boundary. A component that only *wraps* kendo-toolbar, or projects tools
 * into it, was tried and disproven by rendered test in PR 2329 iteration 2.
 * The shape that works, and the one this component uses, is a single template
 * that owns BOTH kendo-editor AND its kendo-toolbar together, so every Kendo
 * content query stays inside one view. Do NOT split the toolbar back out to a
 * consumer without re-running the render experiment against the then-current
 * Kendo. The `work-scope-editor.component.spec.ts` render test is that guard.
 *
 * The tool set is locked (29840): bold/italic/underline/strikethrough,
 * unordered/ordered list + indent/outdent, undo/redo. No image, font, color,
 * format, view-source or file tools, because the proposal template owns
 * typography and images do not belong on a customer-facing scope.
 *
 * Dual binding contract so both consumer styles work unchanged:
 *   - `formControlName` (the two SRF sites) via ControlValueAccessor
 *   - `[value]` + `(valueChange)` (the EB store site, which feeds an already
 *     HTML-normalized value from its own computed)
 */
@Component({
  selector: 'ups-work-scope-editor',
  imports: [EditorModule],
  templateUrl: './work-scope-editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkScopeEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkScopeEditorComponent
  implements ControlValueAccessor, AfterViewInit
{
  // Constructor-parameter injection (not inject() field initializers) so the
  // CVA specs can keep constructing the component BARE with `new`; both are
  // null there and ngAfterViewInit no-ops.
  constructor(
    private readonly hostRef: ElementRef | null = null,
    private readonly renderer: Renderer2 | null = null
  ) {}

  @ViewChild(EditorComponent) private editorComponent?: EditorComponent;

  /**
   * Applies the 30748 text-assist attributes once the editor exists. A child's
   * ngAfterViewInit completes before its parent's, and, verified against
   * kendo v22 source, with `iframe=false` the editor's initialize() (which
   * constructs the ProseMirror view) runs SYNCHRONOUSLY inside kendo's own
   * ngAfterViewInit: initialize() fires from zip(afterViewInit$,
   * contentAreaLoaded$) and the non-iframe branch emits contentAreaLoaded in
   * the same tick. So the view exists here. TWO CONTINGENCIES that doc pins:
   * this template HARDCODES [iframe]="false" (an iframe editor loads its
   * content area async, which would put this hook BEFORE view creation), and
   * kendo recreates the view ONLY on an iframe input change (value writes go
   * through changeValue, no recreation), so the attributes stick for the
   * component's life. If iframe ever becomes an input here, re-verify both.
   * (The old same-host DIRECTIVE could not rely on any of this ordering,
   * which is why it needed afterNextRender.) Living inside the shared
   * component means all three Work Scope sites get the behavior with no
   * per-consumer wiring to forget.
   */
  ngAfterViewInit(): void {
    if (!this.renderer) {
      // Bare-constructed (specs). In prod DI both params are always supplied;
      // warn in dev so a broken DI path never silently skips the attributes.
      if (isDevMode()) {
        console.warn(
          '[WorkScopeEditor] no Renderer2; text-assist attributes not applied.'
        );
      }
      return;
    }
    const contentEl =
      (this.editorComponent?.view?.dom as HTMLElement | undefined) ??
      (
        this.hostRef?.nativeElement as HTMLElement | undefined
      )?.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!contentEl) {
      // A silent no-op is a debugging trap: the field would just stop
      // auto-capitalizing on mobile with nothing to point at. Surface in dev.
      if (isDevMode()) {
        console.warn(
          '[WorkScopeEditor] could not resolve the kendo-editor contenteditable; auto-capitalize not applied.'
        );
      }
      return;
    }
    applyEditorAutocapitalizeAttrs(contentEl, this.renderer);
  }

  /**
   * Class applied to the inner kendo-editor, which selects the sizing rule in
   * the app's global styles.scss: 'ups-workscope-editor' (SRF, ~14rem) or
   * 'eb-workscope' (EB, ~7rem plus bullet first-letter caps). Kept as an input
   * because the two consumers deliberately differ in height and the EB variant
   * carries the 29849 list-caps rule; the classes are mutually exclusive so
   * neither consumer's look changes.
   */
  @Input() editorClass = 'ups-workscope-editor';

  /**
   * value/valueChange contract for non-form consumers (Estimate Builder store).
   *
   * WI 31903: `value` is written BOTH by the `[value]` input and imperatively by
   * the CVA `writeValue()` / `onValueChange()` paths. Under OnPush the CVA writes
   * bypass input change detection, so the value is backed by a signal
   * (`workScope`) whose `.set()` re-renders the editor. The `value` getter/setter
   * keep the public `value` property and the `[value]` binding contract intact
   * (so consumers and the CVA specs are unaffected). `workScope` is a plain
   * `signal()`, not `input()`, so the bare-`new` CVA specs keep constructing the
   * component without an injection context.
   */
  readonly workScope = signal('');

  @Input()
  set value(v: string) {
    this.workScope.set(v);
  }
  get value(): string {
    return this.workScope();
  }

  @Output() valueChange = new EventEmitter<string>();

  protected readonly disabled = signal(false);

  onTouched: () => void = () => {
    // replaced by registerOnTouched when used as a form control
  };
  private onChange: (value: string) => void = () => {
    // replaced by registerOnChange when used as a form control
  };

  writeValue(value: string): void {
    this.workScope.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onValueChange(value: string): void {
    this.workScope.set(value);
    this.valueChange.emit(value);
    this.onChange(value);
  }
}
